<script lang="ts">
  import { getFormStyle, styleToString } from "@shared/common/node/style";
  import type {
    IFormImageChoiceNode,
    IFormNode,
    PartialNode,
  } from "@shared/common/node/type";
  import type { ICampaignSetting } from "@shared/common/types/campaign";
  import type {
    IFileSubmission,
    IImageChoiceSubmission,
    ISimpleChoiceSubmission,
    Submission,
  } from "@shared/common/types/form";
  import {
    extractFormChoiceValue,
    generateFormDataName,
    generateHiddenFieldDataName,
    getImageLabelFromIndex,
    hasSubmission,
  } from "@shared/common/utils/form";
  import { getContext, onDestroy, onMount } from "svelte";
  import detectReaction from "../../actions/detect-reaction";
  import {
    getCurrentDocumentNode,
    getFormContext,
    getStore,
  } from "../../context";

  const campaignSetting = getContext<ICampaignSetting>("campaignSetting");

  const { formElement, reactionEventListener, formNode } = getStore();

  const documentNode = getCurrentDocumentNode();

  const {
    onSubmit,
    onUpload,
    onStateChange,
    checkCustomValidity,
    itemNodeMap,
    width,
  } = getFormContext();

  export let node: PartialNode<IFormNode>;
  formNode.set(node);
  let isSubmitting = false;

  function getHiddenFieldSubmission(formData: FormData, name: string): string {
    const value = formData.get(name);
    return `${value}`;
  }

  function getDefaultSubmission(
    formData: FormData,
    name: string
  ): string | string[] {
    const values = formData.getAll(name);
    if (values.length === 1) {
      return `${values[0]}`;
    }
    return values.map((value) => `${value}`);
  }

  function getSimpleChoiceSubmission(
    formData: FormData,
    name: string
  ): ISimpleChoiceSubmission {
    const values = formData.getAll(name);

    return {
      type: "SIMPLE_CHOICE_SUBMISSION",
      choices: values.map((value) => {
        const { id, label } = extractFormChoiceValue(String(value));
        return { id, label };
      }),
    };
  }

  function getImageChoiceSubmission(
    formData: FormData,
    name: string,
    node: PartialNode<IFormImageChoiceNode>
  ): IImageChoiceSubmission {
    const checkedLabels = formData
      .getAll(name)
      .map((itemLabel) => `${itemLabel}`);

    return {
      type: "IMAGE_CHOICE_SUBMISSION",
      choices: (node.items || [])
        .map((item, index) => ({
          id: item.id ?? "",
          label: getImageLabelFromIndex(index) ?? "",
          url: item.url ?? "",
        }))
        .filter((item) => checkedLabels.includes(item.label)),
    };
  }

  async function getAndUploadFileSubmission(
    formData: FormData,
    name: string
  ): Promise<IFileSubmission> {
    const values = formData.getAll(name);

    if (
      !values.every(
        (value): value is File => value instanceof File && value.name !== ""
      )
    ) {
      return {
        type: "FILE_SUBMISSION",
        files: [],
      };
    }

    const res = await onUpload({ files: values });

    return {
      type: "FILE_SUBMISSION",
      files: values.map((value: File) => {
        const extension = value.name.split(".").pop() || "";

        const file = (res ?? []).find(
          (item) => item.originalFilename === value.name
        );

        return {
          originFilename: value.name,
          filename: file?.filename ?? "",
          size: value.size,
          extension,
          url: file?.url ?? "",
          type: value.type,
        };
      }),
    };
  }

  /** @see https://developer.mozilla.org/en-US/docs/Learn/Forms/Form_validation */
  async function handleSubmit() {
    if (!$formElement) {
      // eslint-disable-next-line no-console
      console.error("form element is not exist");
      return;
    }
    if (isSubmitting) {
      return;
    }
    isSubmitting = true;

    const validity = checkCustomValidity($formElement);
    if (!validity) {
      $formElement.reportValidity();
      isSubmitting = false;
      await onStateChange({ state: "idle" });
      return;
    }

    const formData = new FormData($formElement || undefined);
    const data: Record<string, Submission> = {};

    for (const name of formData.keys()) {
      const node = itemNodeMap.get(name);
      if (!node || !node.type || !hasSubmission(node)) {
        continue;
      }

      switch (node.type) {
        case "FORM_IMAGE_CHOICE":
          data[name] = getImageChoiceSubmission(formData, name, node);
          break;

        case "FORM_CHOICE":
        case "FORM_DROPDOWN":
        case "FORM_SEAN_ELLIS":
          data[name] = getSimpleChoiceSubmission(formData, name);
          break;

        case "FORM_RATING_SCALE":
        case "FORM_NPS": {
          const value = `${formData.getAll(name)[0]}`;
          data[name] = Number.parseInt(value);
          break;
        }

        case "FORM_TEXTAREA":
        case "FORM_INPUT":
        case "FORM_PRIVACY_POLICY":
          data[name] = getDefaultSubmission(formData, name);
          break;

        case "FORM_FILE_UPLOADER":
          if (data[name]) {
            // 파일 중복 업로드 방지
            break;
          }
          await onStateChange({ state: "uploading" });
          data[name] = await getAndUploadFileSubmission(formData, name);
          break;

        case "FORM_HIDDEN_FIELD":
          data[name] = getHiddenFieldSubmission(formData, name);
          break;
      }
    }
    await onSubmit({
      data,
      node,
      element: $formElement,
    });
    isSubmitting = false;
    await onStateChange({ state: "idle" });
  }

  let resizeObserver = new ResizeObserver(() => {
    $width = $formElement?.clientWidth || 0;
  });

  onMount(() => {
    node.children?.forEach((child) => {
      if (child.type === "FORM_HIDDEN_FIELD") {
        child.fields?.forEach((field) => {
          itemNodeMap.set(generateHiddenFieldDataName(field), child);
        });
      } else {
        const name = generateFormDataName(child);
        itemNodeMap.set(name, child);
      }
    });

    if ($formElement) {
      resizeObserver.observe($formElement);
    }
  });

  onDestroy(() => {
    node.children?.forEach((child) => {
      const name = generateFormDataName(child);
      itemNodeMap.delete(name);
    });

    resizeObserver.disconnect();
  });
</script>

<form
  on:submit|preventDefault={handleSubmit}
  bind:this={$formElement}
  use:detectReaction={{
    node,
    documentNode,
    onAction: reactionEventListener,
  }}
  style={styleToString(getFormStyle(node, campaignSetting.language))}
>
  <slot />
</form>
